import React from "react";
import { Accordion, AccordionTab } from "primereact/accordion";
import { getName } from "country-list";
import crossIcon from "../../assets/images/cross-icon.png";
import checkIcon from "../../assets/images/tick-icon.png";
import { PayslipStatusMap } from "~/utils/fileUtils";
import { transformRoleDisplayName } from "~/utils/roleDisplayName";
import { getTenantFieldDisplayName, isTenantField } from "./utils";

const ChangesAccordian = ({
  old_data,
  new_data,
  isEntityAccessAudit = false,
}) => {
  const formatKey = (key) => {
    // Use tenant field mapping for tenant-specific fields
    if (isTenantField(key)) {
      return getTenantFieldDisplayName(key);
    }

    // Fallback to existing formatting logic for non-tenant fields
    const formattedKey = key
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase())
      .toLowerCase()
      .replace(/^\w/, (char) => char.toUpperCase());

    return formattedKey;
  };

  function isValidCountryCode(code) {
    return typeof code === "string" && getName(code) !== undefined;
  }
  const dueDateformatDate = (dateString) => {
    if (dateString) {
      const date = new Date(dateString);
      const day = date.getDate();
      const suffixes = ["th", "st", "nd", "rd"];
      const suffixIndex =
        day % 10 > 3 || Math.floor((day % 100) / 10) === 1 ? 0 : day % 10;
      const suffix = suffixes[suffixIndex];
      return `${date.toLocaleString("en-US", { month: "short" })} ${day}${suffix}, ${date.getFullYear()}`;
    }
    return "-";
  };

  const clean = (key, value) => {
    if (!value && value !== 0 && value !== false) return "";

    // Handle arrays - check if they contain simple values or complex objects
    if (Array.isArray(value)) {
      // Check if array contains only simple values (string, number, boolean)
      const hasSimpleValues = value.every(
        (item) =>
          typeof item === "string" ||
          typeof item === "number" ||
          typeof item === "boolean" ||
          item === null ||
          item === undefined,
      );

      if (hasSimpleValues) {
        // Render simple arrays as comma-separated list
        return value
          .filter((item) => item !== null && item !== undefined)
          .map((item) => clean(key, item))
          .join(", ");
      }

      // Special handling for Services arrays (entity access audit)
      // Format: [{ serviceId, serviceName, processCategories }] or [{ serviceId, name, processCategories }]
      if (key === "services" && value.length > 0) {
        const isServiceArray = value.some(
          (item) =>
            item &&
            typeof item === "object" &&
            (item.serviceName || item.name || item.serviceId),
        );
        if (isServiceArray) {
          return value
            .map((service) => {
              const serviceName =
                service.serviceName || service.name || "Unknown Service";
              // For entity access audit, don't show processCategories
              if (isEntityAccessAudit) {
                return serviceName;
              }
              // For other audits, show processCategories
              const processCategories = service.processCategories || [];
              const categoriesText =
                processCategories.length > 0
                  ? ` (${processCategories.join(", ")})`
                  : "";
              return `${serviceName}${categoriesText}`;
            })
            .join("; ");
        }
      }

      // Array contains objects - stringify with formatting
      return JSON.stringify(value, null, 2);
    }

    // Handle objects - stringify complex objects, but keep simple types as-is
    if (typeof value === "object" && value !== null) {
      // Check if it's a plain object (not Date, etc.)
      if (value.constructor === Object) {
        // Stringify objects with 2-space indentation for readability
        return JSON.stringify(value, null, 2);
      }
      // For other object types (Date, etc.), convert to string
      return String(value);
    }
    if (key === "start_date" || key === "end_date") {
      return new Date(value).toLocaleDateString();
    }
    if (
      [
        "due_date",
        "acquisition_date",
        "sale_closure_date",
        "incorporation_date",
        "date_of_birth",
        "expiration_date",
        "expiresAt",
        "expires_at",
        "in_progress_at",
        "completed_at",
        "expiry_date",
      ].includes(key)
    ) {
      return dueDateformatDate(value);
    }
    if (isValidCountryCode(value) && key.includes("country")) {
      return getName(value);
    }
    if (typeof value === "boolean") {
      // Format booleans as Active/Inactive with icons for status fields
      if (
        key.includes("active") ||
        key.includes("status") ||
        key.includes("enabled")
      ) {
        return value ? (
          <span className="history-enable-icon-container">
            Active{" "}
            <img className="history-enable-icon" src={checkIcon} alt="Active" />
          </span>
        ) : (
          <span className="history-enable-icon-container">
            Inactive{" "}
            <img
              className="history-enable-icon"
              src={crossIcon}
              alt="Inactive"
            />
          </span>
        );
      }
      return value.toString();
    }
    if (value === "approved" && key === "match_status") {
      return PayslipStatusMap[value] || "Approved";
    }
    if (value === "rejected" && key === "match_status") {
      return PayslipStatusMap[value] || "Rejected";
    }
    if (
      (value === "matched" ||
        value === "unmatched" ||
        value === "no_match" ||
        value === "extraction_failed") &&
      key === "match_status"
    ) {
      return "Awaiting Review";
    }
    if (value === "disabled") {
      return (
        <span className="history-enable-icon-container">
          Disabled{" "}
          <img className="history-enable-icon" src={crossIcon} alt="Disabled" />
        </span>
      );
    }
    if (value === "enabled") {
      return (
        <span className="history-enable-icon-container">
          Enabled <img src={checkIcon} alt="Enabled" />
        </span>
      );
    }

    // Transform role display names (LSP -> Local Team, etc.)
    return transformRoleDisplayName(value);
  };

  // Ensure old_data and new_data are objects (handle null/undefined)
  const safeOldData = old_data || {};
  const safeNewData = new_data || {};

  // Helper function to compare values (handles dates, arrays, objects)
  const valuesAreEqual = (val1, val2) => {
    // Handle null/undefined
    if (val1 === val2) return true;
    if (val1 == null || val2 == null) return false;

    // Handle arrays - compare as JSON strings for deep equality
    if (Array.isArray(val1) && Array.isArray(val2)) {
      return JSON.stringify(val1) === JSON.stringify(val2);
    }

    // Handle objects - compare as JSON strings for deep equality
    if (
      typeof val1 === "object" &&
      typeof val2 === "object" &&
      !Array.isArray(val1) &&
      !Array.isArray(val2)
    ) {
      return JSON.stringify(val1) === JSON.stringify(val2);
    }

    // Handle dates - compare formatted dates
    if (val1 instanceof Date && val2 instanceof Date) {
      return val1.getTime() === val2.getTime();
    }

    // For expiration dates (formatted like "Jan 7th, 2026"), try to parse and compare
    if (typeof val1 === "string" && typeof val2 === "string") {
      // Check if both look like formatted dates
      const datePattern =
        /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d+(st|nd|rd|th)?,?\s+\d{4}/i;
      if (datePattern.test(val1) && datePattern.test(val2)) {
        try {
          // Try to parse dates - new Date can handle "Jan 7th, 2026" format
          const date1 = new Date(val1);
          const date2 = new Date(val2);
          if (
            !Number.isNaN(date1.getTime()) &&
            !Number.isNaN(date2.getTime())
          ) {
            // Compare dates by day (ignore time)
            const time1 = new Date(
              date1.getFullYear(),
              date1.getMonth(),
              date1.getDate(),
            ).getTime();
            const time2 = new Date(
              date2.getFullYear(),
              date2.getMonth(),
              date2.getDate(),
            ).getTime();
            return time1 === time2;
          }
        } catch (e) {
          // If parsing fails, fall through to string comparison
        }
      }
    }

    // Default: string comparison
    return String(val1) === String(val2);
  };

  // Filter out fields where old and new values are the same (for entity access audit)
  const filterUnchangedFields = (oldData, newData) => {
    if (!isEntityAccessAudit) {
      return { oldData, newData };
    }

    const filteredOld = {};
    const filteredNew = {};

    // Get all unique keys from both objects
    const allKeys = new Set([...Object.keys(oldData), ...Object.keys(newData)]);

    allKeys.forEach((key) => {
      const oldVal = oldData[key];
      const newVal = newData[key];

      // Only include if values are different
      if (!valuesAreEqual(oldVal, newVal)) {
        if (oldVal !== undefined) filteredOld[key] = oldVal;
        if (newVal !== undefined) filteredNew[key] = newVal;
      }
    });

    return { oldData: filteredOld, newData: filteredNew };
  };

  const { oldData: filteredOldData, newData: filteredNewData } =
    filterUnchangedFields(safeOldData, safeNewData);

  const hasOldData = Object.keys(filteredOldData).length > 0;
  const hasNewData = Object.keys(filteredNewData).length > 0;

  return (
    <div className="changes-accordian">
      <Accordion>
        <AccordionTab header="Compare Changes">
          <div className="change-card new-changes">
            <h6 className="title">Changes</h6>
            {hasNewData ? (
              Object.entries(filteredNewData).map(([key, value]) => {
                const displayValue = clean(key, value);
                // Handle both string and JSX return values
                const renderedValue =
                  typeof displayValue === "string"
                    ? decodeURIComponent(displayValue)
                    : displayValue;
                return (
                  <div key={`new-${key}`} className="changes-overflow">
                    <span>{formatKey(key)}:</span> <span>{renderedValue}</span>
                  </div>
                );
              })
            ) : (
              <div className="changes-overflow">
                <span>No changes</span>
              </div>
            )}
          </div>

          {hasOldData && hasNewData && <span className="divider" />}
          <div className="change-card old-changes">
            <h6 className="title">Previous</h6>
            {hasOldData ? (
              Object.entries(filteredOldData).map(([key, value]) => {
                const displayValue = clean(key, value);
                // Handle both string and JSX return values
                const renderedValue =
                  typeof displayValue === "string"
                    ? decodeURIComponent(displayValue)
                    : displayValue;
                return (
                  <div key={`old-${key}`} className="changes-overflow">
                    <span>{formatKey(key)}:</span> <span>{renderedValue}</span>
                  </div>
                );
              })
            ) : (
              <div className="changes-overflow">
                <span>No previous data</span>
              </div>
            )}
          </div>
        </AccordionTab>
      </Accordion>
    </div>
  );
};

export default ChangesAccordian;
